// The change navigator: turns the counts in the Crux header into a way of
// moving through the diff, for every format.
//
// Division of labour — the renderers publish COORDINATES they already know
// (`data-ln-l` / `data-ln-r`, `data-span`, `data-key`, `data-path`,
// `data-addr`, `data-column`) and know nothing about navigation; the engine's
// `changeStops()` says where the places are, in those same coordinates; this
// module is the only code that resolves one to a DOM node, reveals it and
// highlights it. Adding a view means publishing coordinates from it, not
// teaching it about stops.
//
// Two rules it exists to keep:
//   G80 — a count and a stop are different units. The card keeps saying
//         "95 words added"; the position readout says "Location 2 of 7".
//   G81 — a count you can click must have somewhere to go. A category with no
//         stops is rendered disabled rather than silently inert.
import { CATEGORY_LABELS } from '../render/summary.js';
import {
  RECORD_VIRTUAL_ROW_HEIGHT,
  TABLE_RECORD_HEIGHT,
  RECORD_VIRTUAL_WINDOW,
} from '../render/records-view.js';

const HIGHLIGHT = 'nav-target';

export function createChangeNav({ root, body, onNeedView, onFilter = null, reduceMotion = () => false }) {
  let model = null;
  let category = null; // the category the stepper is cycling
  let index = -1; // position WITHIN that category's stopIndexes
  let pending = null; // the stop we are currently trying to reach, across a re-render
  let filtered = false; // whether a category filter is currently narrowing the diff

  const el = (sel) => root.querySelector(sel);
  const nav = el('#cruxNav');
  const pos = el('#navPos');
  const prev = el('#navPrev');
  const next = el('#navNext');
  const clear = el('#cruxShowAll');

  function stopsOf(id) {
    if (id == null || id === 'all') return model?.stops.map((s) => s.index) || [];
    return model?.categories.find((c) => c.id === id)?.stopIndexes || [];
  }

  function setModel(nextModel) {
    model = nextModel;
    category = null;
    index = model?.stops.length ? 0 : -1;
    pending = model?.stops[0] || null;
    filtered = false;
    const navigable = (model?.categories || []).filter((c) => c.stopIndexes.length);
    nav.hidden = navigable.length === 0;
    if (clear) clear.hidden = true;
    // Disable a card that has a total but nowhere to go — `moved` on a record
    // set is the order-changed footnote, a real number with no place to stand.
    for (const card of root.querySelectorAll('.stat[data-category]')) {
      if (card.tagName !== 'BUTTON') continue;
      // The leading card is not a category — it counts every place, so it is
      // live whenever anything is navigable at all. Asking stopsOf('all') for
      // its stops finds no such category and would disable the one control
      // that clears a filter.
      if (card.dataset.category === 'all') {
        card.disabled = navigable.length === 0;
        card.setAttribute('aria-pressed', 'true');
        const n = model?.stops.length || 0;
        card.title = `Review all ${n} locations`;
        continue;
      }
      const has = stopsOf(card.dataset.category).length > 0;
      card.disabled = !has;
      card.setAttribute('aria-pressed', 'false');
      if (has) {
        const n = stopsOf(card.dataset.category).length;
        card.title = `Review ${n} ${n === 1 ? 'location' : 'locations'}`;
      } else {
        card.title = 'No place to jump to — this is a summary note, not a location';
      }
    }
    render();
  }

  function render() {
    const list = category ? stopsOf(category) : model?.stops.map((s) => s.index) || [];
    const active = index >= 0 && index < list.length;
    // Keep boundary controls focusable so reaching the end never drops focus.
    prev.setAttribute('aria-disabled', String(!active || index === 0));
    next.setAttribute('aria-disabled', String(!list.length || index >= list.length - 1));
    const total = list.length;
    const stop = active ? model.stops[list[index]] : null;
    const loc = stop?.location;
    let detail = stop?.label || '';
    if (loc?.kind === 'record') detail = `Record ID ${loc.recordKey}`;
    if (loc?.kind === 'path') detail = loc.path || '(root)';
    if (loc?.kind === 'move') detail = `Lines ${loc.fromStart}–${loc.fromEnd} → ${loc.toStart}–${loc.toEnd}`;
    pos.textContent = active
      ? `${CATEGORY_LABELS[category || 'all']} · Location ${index + 1} of ${total}${total === 1 ? ' · Only matching location' : ''}${detail ? ` · ${detail}` : ''}`
      : 'No review locations';
    const selected = category || 'all';
    for (const card of root.querySelectorAll('.stat[data-category]')) {
      card.setAttribute('aria-pressed', String(card.dataset.category === selected));
    }
    if (clear) clear.hidden = !filtered;
  }

  // Step within the SELECTED category. Choosing a category starts at its first
  // stop rather than jumping straight to the second, so the first click and the
  // first Next do not skip a place.
  function step(delta, fromCategory = null) {
    if (!model) return;
    if (fromCategory && fromCategory !== category) {
      category = fromCategory;
      index = -1;
      // Where the diff is a ledger, selecting a category narrows it as well as
      // stepping through it — the host answers whether it could.
      filtered = onFilter ? onFilter(category) === true : false;
    }
    const list = category ? stopsOf(category) : model.stops.map((s) => s.index);
    if (!list.length) return;
    index = index < 0 ? 0 : Math.max(0, Math.min(list.length - 1, index + delta));
    goTo(list[index]);
    render();
  }

  // The leading card: every place, in document order, and no filter. This is the
  // way back from a filtered ledger, so it must clear BOTH the narrowing and the
  // category — "Review all changes" that left a filter on would be a lie.
  function reviewAll({ move = true } = {}) {
    if (!model) return;
    category = null;
    index = model.stops.length ? 0 : -1;
    pending = model.stops[0] || null;
    if (filtered && onFilter) onFilter(null);
    filtered = false;
    if (move && model.stops.length) {
      index = 0;
      category = null;
      goTo(0);
      index = 0;
    }
    if (!move) restore();
    render();
  }

  function goTo(stopIndex) {
    const stop = model?.stops[stopIndex];
    if (!stop) return;
    pending = stop;
    reveal(stop, 0, { view: true, scroll: true });
  }

  // Resolve → reveal → correct. `attempt` bounds the work: one paint to let a
  // virtualized window mount, one view change, then give up rather than loop.
  // `opts.view` gates the view change and `opts.scroll` the scroll, because the
  // two callers want different things: an explicit jump may do both, while
  // re-finding the place after a re-render must do NEITHER — overriding the
  // view the reader just picked, or yanking their scroll position, would make
  // the navigator fight its own user.
  function reveal(stop, attempt, opts = { view: true, scroll: true }) {
    const seeked = seekVirtual(stop);
    const targets = find(stop);
    if (targets.length) {
      mark(targets, opts.scroll);
      return;
    }
    clearMarks();
    if (seeked && attempt < 2) {
      requestAnimationFrame(() => reveal(stop, attempt + 1, opts));
      return;
    }
    // Not present in this view at all (Raw has no record keys or leaf paths).
    // Unified lists every change for every format, so switch to it and retry.
    if (opts.view && attempt < 2 && onNeedView && onNeedView('unified')) {
      requestAnimationFrame(() => reveal(stop, attempt + 1, opts));
    }
  }

  function find(stop) {
    const loc = stop.location;
    if (!loc) return [];
    const q = (sel) => Array.from(body.querySelectorAll(sel));
    if (loc.kind === 'span') {
      // Prefer the passage BAND so prose highlights a region like every other
      // format highlights a row; fall back to the individual runs in a view
      // that does not draw bands (Raw).
      const band = q(`[data-passage="${cssStr(stop.id)}"]`);
      if (band.length) return band;
      const out = [];
      for (let i = loc.spanStart; i <= loc.spanEnd; i++) out.push(...q(`[data-span="${cssNum(i)}"]`));
      return out;
    }
    if (loc.kind === 'record') return q(`[data-key="${cssStr(loc.recordKey)}"]`);
    if (loc.kind === 'column') return q(`[data-column="${cssStr(loc.name)}"]`);
    if (loc.kind === 'path') {
      return [...q(`[data-path="${cssStr(loc.path)}"]`), ...q(`[data-addr~="${cssStr(`path:${loc.path}`)}"]`)];
    }
    if (loc.kind === 'move') {
      // The move MARKERS, not the lines they label — both ends, so a move reads
      // as the one thing it is.
      return [...q(`[data-move-r="${cssNum(loc.toStart)}"]`), ...q(`[data-move-l="${cssNum(loc.fromStart)}"]`)];
    }
    // A grouped stop covers the entire range on both sides, not just its
    // first line. Line numbers are renderer coordinates, not CSS offsets.
    const range = (side, start, end) => start == null ? [] : q(`[data-ln-${side}]`).filter((node) => {
      const n = Number(node.getAttribute(`data-ln-${side}`));
      return n >= start && n <= (end ?? start);
    });
    return [...range('r', loc.lineR, loc.lineREnd), ...range('l', loc.lineL, loc.lineLEnd)];
  }

  // Scroll a virtualized list far enough that the target row will mount. The
  // arithmetic uses the SAME row constant the scroller itself windows with, so
  // it is exactly as accurate as the existing scroll handler — and the final
  // scrollIntoView below corrects whatever that estimate got wrong, which is
  // what keeps a wrapped (taller) row from drifting the landing point.
  function seekVirtual(stop) {
    const row = stop.location?.rowIndex;
    if (row == null) return false;
    const list = body.querySelector('[data-record-virtual] .record-virtual-viewport');
    if (list) {
      const h = Number(body.querySelector('[data-record-virtual]')?.dataset.rowHeight) || RECORD_VIRTUAL_ROW_HEIGHT;
      list.scrollTop = Math.max(0, (row - Math.floor(RECORD_VIRTUAL_WINDOW / 4)) * h);
      list.dispatchEvent(new Event('scroll'));
      return true;
    }
    const table = body.querySelector('.record-table-virtual');
    if (table) {
      table.scrollTop = Math.max(0, (row - Math.floor(RECORD_VIRTUAL_WINDOW / 4)) * TABLE_RECORD_HEIGHT);
      table.dispatchEvent(new Event('scroll'));
      return true;
    }
    return false;
  }

  function clearMarks() {
    for (const old of body.querySelectorAll(`.${HIGHLIGHT}`)) {
      old.classList.remove(HIGHLIGHT);
      old.removeAttribute('aria-current');
    }
  }

  function mark(targets, scroll = true) {
    clearMarks();
    for (const t of targets) {
      t.classList.add(HIGHLIGHT);
      t.setAttribute('aria-current', 'true'); // the outline is visual only
    }
    if (!scroll) return;
    // Side-by-side: the same passage sits at two different heights in the two
    // panes, so centre the box that CONTAINS both rather than assuming they
    // line up. If that box is taller than the viewport, show its start.
    const rects = targets.map((t) => t.getBoundingClientRect());
    const top = Math.min(...rects.map((r) => r.top));
    const bottom = Math.max(...rects.map((r) => r.bottom));
    const behavior = reduceMotion() ? 'auto' : 'smooth';
    const viewport = window.innerHeight || 0;
    const offset = bottom - top > viewport * 0.8 ? viewport * 0.15 : (viewport - (bottom - top)) / 2;
    // An ABSOLUTE destination, not a relative scrollBy: pressing Next twice in
    // quick succession leaves a smooth scroll in flight, and a relative delta
    // measured against that moving position lands somewhere between the two
    // places. Document coordinates are stable whatever the animation is doing.
    window.scrollTo({ top: Math.max(0, top + window.scrollY - offset), behavior });
    // Horizontal panes can scroll independently of the page.
    for (const t of targets) {
      const scroller = t.closest('.record-table-scroll, .record-virtual-viewport');
      if (scroller) scroller.scrollTop += t.getBoundingClientRect().top - scroller.getBoundingClientRect().top - scroller.clientHeight / 3;
    }
  }

  // A re-render (view switch, scope switch, ignore rule) destroys the marked
  // node. Re-mark the place the reader was standing — without changing the view
  // they just chose and without moving their scroll. If the new view cannot
  // show that place at all (Raw has no record keys), the highlight is dropped
  // rather than left pointing at whatever row happens to be there now.
  function restore() {
    if (pending) reveal(pending, 0, { view: false, scroll: false });
  }

  prev.addEventListener('click', () => { if (prev.getAttribute('aria-disabled') !== 'true') step(-1); prev.focus(); });
  next.addEventListener('click', () => { if (next.getAttribute('aria-disabled') !== 'true') step(1); next.focus(); });
  clear?.addEventListener('click', () => { reviewAll({ move: false }); clear.focus(); });

  root.addEventListener('click', (e) => {
    const card = e.target.closest('.stat[data-category]');
    if (!card || card.disabled || !root.contains(card)) return;
    if (card.dataset.category === 'all') reviewAll();
    else if (category !== card.dataset.category) step(1, card.dataset.category);
    card.focus(); // repeated activation keeps working without chasing focus
  });

  // n / p anywhere in the results area, never inside an editable field — the
  // two input panes are textareas and must keep their own letters.
  function onKey(e) {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
    if (nav.hidden) return;
    const active = document.activeElement;
    if (!active || (!root.contains(active) && !body.contains(active))) return;
    const k = e.key.toLowerCase();
    if (k !== 'n' && k !== 'p') return;
    e.preventDefault();
    step(k === 'n' ? 1 : -1);
  }
  document.addEventListener('keydown', onKey);

  return { setModel, restore, step, reviewAll };
}

// Attribute selectors take arbitrary user data (a record key, a leaf path), so
// both halves go through CSS.escape — a key containing a quote must not be able
// to change the selector it appears in.
const cssStr = (v) => (window.CSS?.escape ? CSS.escape(String(v)) : String(v).replace(/["\\\]]/g, '\\$&'));
const cssNum = (v) => String(Number(v));
